import { Command } from 'commander'
import { dump } from 'js-yaml'
import * as path from 'path'
import YamStateLocker from 'yam-operator/engine/locker'
import { CACHE_API_PATH, ENGINE_BASE_PATH } from 'yam-operator/kube/kube-client'
import { OperatorParam } from 'yam-operator/types'
import { createLogger, fsRemove, generateWorkloadNameWithEnv, parseBaseType, splitAndTrimComma, switchToFileLogMode } from 'yam-operator/util'
import { COMMON_CACHE_PATH } from 'yam-operator/util/cache'
import { KV, PlanContext } from 'yam-types'
import { applyAppBundle, deleteParameterValues, engineSettings, generateModel, getParameterValues, listParameterValues, planApply, PlanApplyOption, previewTemplate, promptDefaultCluster, setParameterValues, ValuesOption } from '../ops/engine'
import { createKubeClient4Cmd } from '../ops/kube'
import { startServer } from '../server'

const log = createLogger('cli')

export function addOperationCommands(program: Command): void {

  program.command('init')
    .argument('<workload>', 'The workload to operate, eg. my-team/my-app')
    .action((workload) => {
      generateModel(workload)
    })

  program.command('plan')
    .option('-v, --version [version]', 'The image tag/version of the workload')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-c, --clusters [clusters]', 'The Kubernetes clusters to deploy the workload, separate by comma')
    .option('--skip-diff [skipDiff]', 'Not perform Diff from previous model to current model, sync everything defined in the model')
    .option('--filter [filter]', 'Plugin filters, when specified, only selected plugins will be enabled')
    .option('--dry-run [dryRun]', 'Not connect to Kubernetes API server nor perform real actions')
    .option('-s, --set [set]', 'Set parameter values from command line args, it will override parameter in values directory, eg. key1=value2,key2=value2')
    .option('--values-from [valuesFrom]', 'Set parameter values from directories or files, eg. --values-from=/path/to/values-dir,  or --values-from values-suffix.yaml,values-another-suffix.yaml')
    .option('-f, --file-out [planFile]', 'The specify the plan file path and name')
    .action(async (opts: PlanApplyOption) => {
      const success = await planApply("plan-only", opts)
      !success && process.exit(-1)
    })

  program.command('apply')
    .option('-v, --version [version]', 'The image tag/version of the workload')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-c, --clusters [clusters]', 'The Kubernetes clusters to deploy the workload, separate by comma')
    .option('--skip-diff [skipDiff]', 'Not perform Diff from previous model to current model, sync everything defined in the model')
    .option('--filter [filter]', 'Plugin filters, when specified, only selected plugins will be enabled')
    .option('--dry-run [dryRun]', 'Not connect to Kubernetes API server nor perform real actions')
    .option('-s, --set [set]', 'Set parameter values from command line args, it will override parameter in values directory, eg. key1=value2,key2=value2')
    .option('--values-from [valuesFrom]', 'Set parameter values from directories or files, eg. --values-from=/path/to/values-dir,  or --values-from values-suffix.yaml,values-another-suffix.yaml')
    .option('-f, --plan-file [planFile]', 'The specify the plan file path and name')
    .option('-w, --wait [waitSeconds]', 'Wait workload Pods to be ready, defaults to 120 seconds, if you don\'t want to perform status check, set to zero', '120')
    .option('-R, --force-restart [forceRestart]', 'Force restart workload')
    .action(async (opts: PlanApplyOption) => {
      const success = await planApply(opts.planFile ? "apply-only" : "plan-apply", opts)
      !success && process.exit(-1)
    })

  program.command('template')
    .option('-v, --version [version]', 'The image tag/version of the workload')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-o, --output-log [output]', 'output log file, defaults to template-output.<date>.log')
    .option('-c, --clusters [clusters]', 'The Kubernetes cluster to render the template, note that only one cluster supported when running in template mode')
    .option('--skip-diff [skipDiff]', 'Not perform Diff from previous model to current model, sync everything defined in the model')
    .option('--filter [filter]', 'Plugin filters, when specified, only selected plugins will be enabled')
    .option('--values-from [valuesFrom]', 'Set parameter values from directories or files, eg. --values-from=/path/to/values-dir,  or --values-from values-suffix.yaml,values-another-suffix.yaml')
    .option('-s, --set [set]', 'Set parameter values from command line args, it will override parameter in values directory, eg. key1=value2,key2=value2')
    .action(async (opts: PlanApplyOption) => {
      if (opts.clusters?.indexOf(',') !== -1) {
        log.error('multiple clusters not supported when running in template mode.')
        process.exit(-1)
      }
      opts.dryRun = true
      opts.templateOutput = true
      const logFile = await switchToFileLogMode((opts as KV).output as string || 'template-output')
      try {
        await planApply("plan-apply", opts, true)
      } catch (ex) {
        console.error(`\n!!! [ERROR] Failed to render templates: ${ex.message}. Please check log file: ${logFile}.`)
        setTimeout(() => {
          process.exit(-1)
        }, 200)
      }
    })

  const valuesCommand = program.command('values')
  valuesCommand.command("list")
    .argument('[variables]', 'The key names of the variable to filter')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--values-from [valuesFrom]', 'Additional values source file or directory')
    .option('-e, --env [env]', 'Environment Locator, could be the prefix of environment full name')
    .action(async (variables: string, opts: ValuesOption) => {
      try {
        const { varDefinitions } = await listParameterValues(opts)
        console.log('\n=============================================== Parameter List ===============================================\n')
        const props = ['name', 'type', 'current', 'merged', 'filename', 'builtIn']
        if (variables) {
          const filters = variables.split(',')
          const filteredList = varDefinitions.filter(v => filters.includes(v.name))
          console.table(filteredList, props)
        } else {
          console.table(varDefinitions, props)
        }
      } catch (ex) {
        log.error(`\n[ERROR] Failed to list parameters: ${ex.message}.`)
        if (log.isLevelEnabled("debug")) {
          log.error(ex.stack)
        }
        process.exit(-1)
      }
    })
  valuesCommand.command("get")
    .argument('[variables]', 'The key name of the parameters')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--values-from [valuesFrom]', 'Additional values source file or directory')
    .description('get customized values of the parameter in all environments')
    .action(async (variables: string, opts: ValuesOption) => {
      try {
        opts.variables = variables
        if (!opts.filename) {
          opts.filename = 'values/params.yaml'
        }
        const obj = await getParameterValues(opts)
        if (!obj) {
          console.log(`[warn] the parameter '${variables}' can not be found`)
        } else {
          console.log('\n=============================================== Parameter Values ===============================================')
          console.log(dump(obj))
        }
      } catch (ex) {
        log.error(`\n[ERROR] Failed to get parameters: ${ex.message}.`)
        if (log.isLevelEnabled("debug")) {
          log.error(ex.stack)
        }
        process.exit(-1)
      }
    })
  valuesCommand.command("set")
    .argument('<variables>', 'The key value pair of the variable to set, eg key=value')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--values-from [valuesFrom]', 'Additional values source')
    .option('-f, --filename [filename]', 'The file to write the variable into, will not take effect for existing variables')
    .option('-e, --env [env]', 'Environment Locator, could be the prefix of environment full name')
    .action(async (variables: string, opts: ValuesOption) => {
      try {
        const vars = {} as KV
        variables.split(',').map(x => x.trim()).filter(x => x).map(x => {
          const tmp = x.split('=')
          vars[tmp[0].trim()] = parseBaseType(tmp[1] || '')
        })
        opts.variables = vars
        if (!opts.filename) {
          opts.filename = 'values/params.yaml'
        }
        await setParameterValues(opts)
      } catch (ex) {
        log.error(`\n[ERROR] Failed to set parameters: ${ex.message}.`)
        if (log.isLevelEnabled("debug")) {
          log.error(ex.stack)
        }
        process.exit(-1)
      }
    })
  valuesCommand.command("del")
    .argument('<variables>', 'The key names that need to be deleted, split by comma when multiple')
    .option('--values-from [valuesFrom]', 'Additional values source')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-f, --filename [filename]', 'The file to delete the variable, defaults to values/params.yaml')
    .option('-e, --env [env]', 'Environment Locator, could be the prefix of environment full name')
    .action(async (variables: string, opts: ValuesOption) => {
      try {
        opts.variables = variables
        if (!opts.filename) {
          opts.filename = 'values/params.yaml'
        }
        await deleteParameterValues(opts)
      } catch (ex) {
        log.error(`\n[ERROR] Failed to delete parameters: ${ex.message}.`)
        if (log.isLevelEnabled("debug")) {
          log.error(ex.stack)
        }
        process.exit(-1)
      }
    })

  program.command("preview")
    .argument('<template>', 'The template file name or directory to render')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--values-from [valuesFrom]', 'Additional values source')
    .option('-e, --env [env]', 'The target environment you want to render')
    .action(async (template, opts: ValuesOption) => {
      try {
        opts.filename = template
        const contentKV = await previewTemplate(opts)
        for (const previewResult of contentKV) {
          console.log(`\n# ============================== ${previewResult.file} ============================`)
          console.log(previewResult.content)
          if (previewResult.missingKeys.length > 0) {
            console.log(`\n# [WARN] The following parameters used in the template are not specified with custom values.`)
            console.log(dump(previewResult.missingKeys))
          }
        }
      } catch (ex) {
        log.error(`\n[ERROR] Failed to preview templates: ${ex.message}.`)
        if (log.isLevelEnabled("debug")) {
          log.error(ex.stack)
        }
        process.exit(-1)
      }
    })

  program.command('uninstall')
    .argument('[workload]', 'The workload to be uninstalled')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--dry-run [dryRun]', 'Not connect to Kubernetes API server nor perform real actions')
    .option('-c, --clusters [clusters]', 'The Kubernetes clusters to uninstall the workload, separate by comma')
    .action(async (workload, opts: PlanApplyOption) => {
      const success = await planApply("uninstall", opts, false, workload)
      !success && process.exit(-1)
      log.info(`uninstall finished`)
    })

  program.command('rollback')
    .argument('<workload>', 'The workload to be uninstalled')
    .option('-c, --clusters [clusters]', 'The Kubernetes clusters to uninstall the workload, separate by comma')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-r, --rev [revision]', `Rollback revision, defaults to '-1', could be '-1' or '-2'`)
    .action((workload, { clusters, revision, dir }) => {
      // TODO rollback, rollback could only happen when logged into cloud, with previous cloud side plan file
      log.info(`uninstalling '${workload}' from env: ${clusters}, rollback to: ${revision}, running in directory: ${dir}`)
    })

  program.command('unlock')
    .argument('[workload]', "The application name")
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-c, --clusters [clusters]', 'The kubernetes context running on')
    .action(async (workload, opts: PlanApplyOption) => {
      const clusterAll = splitAndTrimComma(opts.clusters)
      if (clusterAll.length === 0) {
        const { fetched, cluster } = await promptDefaultCluster()
        if (!fetched) {
          throw new Error('please specify clusters to unlock')
        }
        if (cluster) {
          clusterAll.push(cluster?.name)
        }
      }
      for (const cluster of clusterAll) {
        const { kubeClient, app } = await createKubeClient4Cmd(workload, cluster, opts.dir)
        const locker = new YamStateLocker({
          engine: engineSettings
        } as OperatorParam, kubeClient)
        await locker.unlock({ data: { workloadName: generateWorkloadNameWithEnv(app, kubeClient.getCurrentCluster().name) } } as PlanContext)
      }
    })

  program.command('up')
    .argument('[catalog]', 'Catalog file, the bundle of application models', 'catalog.yaml')
    .option('-c, --clusters [clusters]', 'The Kubernetes clusters to deploy the application bundle, separate by comma')
    .option('-i, --include [include]', 'Include mode, only install selected applications')
    .option('-e, --exclude [exclude]', 'Exclude mode, install all applications except excluded items')
    .option('--skip-diff [skipDiff]', 'Not perform Diff from previous model to current model, sync everything defined in the model')
    .option('--filter [filter]', 'Plugin filters, when specified, only selected plugins will be enabled')
    .option('--dry-run [dryRun]', 'Not connect to Kubernetes API server nor perform real actions')
    .option('-s, --set [set]', 'Set parameter values from command line args, it will override parameter in values directory, eg. key1=value2,key2=value2')
    .option('--values-from [valuesFrom]', 'Set parameter values from directories or files, eg. --values-from=/path/to/values-dir,  or --values-from values-suffix.yaml,values-another-suffix.yaml')
    .option('-R, --force-restart [forceRestart]', 'Force restart workload')
    .option('-w, --wait [waitSeconds]', 'For each app in catalog, wait workload Pods to be ready, defaults to 120 seconds, if you don\'t want to perform status check, set to zero \'-w 0\'', '120')
    .action(async (bundles: string, options: PlanApplyOption) => {
      const success = await applyAppBundle(bundles, options)
      !success && process.exit(-1)
    })

  const serve = async ({ dir, port, bind }: { dir?: string, port?: string, bind?: string }) => {
    const portNum = parseInt(port || process.env.PORT || '3000')
    if (isNaN(portNum) || portNum < 80 || portNum > 65535) {
      log.error('invalid port')
      return
    }
    if (dir) {
      log.info(`starting server to display application model in directory: ${dir}`)
    }
    await startServer(portNum, bind)
    log.info(`server started, please goto: http://localhost:${portNum}`)
  }
  program.command('ui')
    .option('-d, --dir [dir]', 'The directory that contains YAM file', './')
    .option('-p, --port [port]', 'The port to listen and serve', '3000')
    .option('-b, --bind [bind]', 'The bind address of the server, defaults to 127.0.0.1, localhost only')
    .action(serve)
  program.command('serve')
    .option('-p, --port [port]', 'The port to listen and serve', '3000')
    .option('-b, --bind [bind]', 'The bind address of the server, defaults to 127.0.0.1, localhost only')
    .action(serve)

  program.command('cache-clean')
    .action(async () => {
      try {
        await fsRemove(COMMON_CACHE_PATH)
        await fsRemove(CACHE_API_PATH)
        await fsRemove(path.resolve(ENGINE_BASE_PATH, 'helm-info-cache.json'))
      } catch (e) {
        log.debug(`cache file delete err msg: ${e.message}`)
      } finally {
        log.info(`all cached files are cleaned`)
      }
    })

}

